/**
 * Viko Call state on the device (pure): the daemon's sealed `call.frame`s plus what happens
 * locally (speech heard, fast-path picks and skips, barge-ins). The queue and the secure batch
 * are the daemon's; local skips and optimistic answers sit on top until a frame confirms them.
 */
import {
  applyCallBriefDelta,
  type CallBrief,
  type CallFrame,
  type CallInterruption,
  type CallQueueItem,
  type CallSecureItem,
  type CallShowTarget,
} from '@viko/protocol'
import type { SpeechPathChoice } from './local-speech.js'

export interface Caption {
  id: string
  who: 'you' | 'viko'
  text: string
  /** The reply is still streaming. */
  live?: boolean
  /** Barge-in: what was heard and what never was. */
  cut?: { heard: string; unheard: string }
  /** Spoken on the device from stored data (fast path), not by the agent. */
  local?: boolean
}

/**
 * - `blocked`: the call could not start yet (content locked, the Mac offline or not approved
 *   here); the panel says why with one primary action (lib/call/problems.ts).
 */
export type CallPhase = 'idle' | 'blocked' | 'starting' | 'live' | 'ended' | 'error'

/** Why a call can't start (or stopped): a known kind, never a raw Error for the UI. */
export interface CallBlocker {
  kind: 'locked' | 'not-approved' | 'offline' | 'mic' | 'failed'
  /** The precise cause, shown only behind "Details". */
  detail?: string
}

export interface CallState {
  phase: CallPhase
  callId?: string
  runId?: string
  machine?: { id: string; name: string }
  speech?: SpeechPathChoice
  startedAt?: number
  agent: 'idle' | 'thinking' | 'speaking'
  listening: boolean
  muted: boolean
  ptt: boolean
  brief?: CallBrief
  queue: CallQueueItem[]
  /** Local overrides: skipped or answered on this device, before the daemon says so. */
  local: Record<string, 'skipped' | 'answered'>
  secure: CallSecureItem[]
  show?: CallShowTarget
  captions: Caption[]
  turnId?: string
  interim: string
  whisper?: 'ready' | 'fetching' | 'unavailable'
  error?: string
  /** Why the call can't start (phase `blocked`). */
  blocker?: CallBlocker
  /** The call view: open (the dock / phone screen) or minimised to the "On call" pill. */
  view: 'open' | 'min'
  /** The daemon sent its queue (until then "nothing needs you" would be a guess). */
  queueSeen: boolean
  endedAt?: number
}

export const initialCall: CallState = {
  phase: 'idle',
  agent: 'idle',
  listening: false,
  muted: false,
  ptt: false,
  queue: [],
  local: {},
  secure: [],
  captions: [],
  interim: '',
  view: 'open',
  queueSeen: false,
}

export type CallEvent =
  | {
      type: 'START'
      callId: string
      machine: { id: string; name: string }
      speech: SpeechPathChoice
      at: number
    }
  | { type: 'STARTED'; runId: string }
  | { type: 'FRAME'; frame: CallFrame }
  | { type: 'INTERIM'; text: string }
  | { type: 'HEARD'; id: string; text: string }
  | { type: 'CUT'; cut: CallInterruption }
  | { type: 'LOCAL_SAY'; id: string; text: string }
  | { type: 'LISTEN'; on: boolean }
  | { type: 'MUTE'; on: boolean }
  | { type: 'PTT'; on: boolean }
  | { type: 'SPEAKING'; on: boolean }
  | { type: 'MARK'; key: string; as: 'skipped' | 'answered' }
  | { type: 'SECURE_STATE'; ids: string[]; state: CallSecureItem['state'] }
  | { type: 'SHOW'; target: CallShowTarget | undefined }
  | { type: 'ENDED'; note?: string; at?: number }
  | { type: 'ERROR'; message: string }
  | { type: 'BLOCKED'; blocker: CallBlocker }
  | { type: 'VIEW'; view: 'open' | 'min' }
  | { type: 'RESET' }

const MAX_CAPTIONS = 60

function addCaption(s: CallState, c: Caption): CallState {
  return { ...s, captions: [...s.captions, c].slice(-MAX_CAPTIONS) }
}

/** The queue as the device shows it: daemon state with local skips/answers applied. */
export function effectiveQueue(s: Pick<CallState, 'queue' | 'local'>): CallQueueItem[] {
  const out = s.queue.map((q) => {
    const l = s.local[q.key]
    return l && (q.state === 'pending' || q.state === 'current') ? { ...q, state: l } : q
  })
  if (!out.some((q) => q.state === 'current')) {
    const i = out.findIndex((q) => q.state === 'pending')
    if (i >= 0) out[i] = { ...out[i]!, state: 'current' }
  }
  // Exactly one current: the first one.
  let seen = false
  return out.map((q) => {
    if (q.state !== 'current') return q
    if (seen) return { ...q, state: 'pending' as const }
    seen = true
    return q
  })
}

/** Answered on this call (confirmed by the daemon or optimistic on this device). */
export function answeredCount(s: Pick<CallState, 'queue' | 'local'>): number {
  return effectiveQueue(s).filter((q) => q.state === 'answered').length
}

export function currentItem(s: Pick<CallState, 'queue' | 'local'>): CallQueueItem | undefined {
  return effectiveQueue(s).find((q) => q.state === 'current')
}

/** "5 of 7": the current item's position among items that need the user on this call. */
export function queueProgress(s: Pick<CallState, 'queue' | 'local'>) {
  const q = effectiveQueue(s).filter((x) => x.kind === 'decision' || x.kind === 'blocker')
  const done = q.filter((x) => x.state !== 'pending' && x.state !== 'current').length
  const cur = q.findIndex((x) => x.state === 'current')
  return { total: q.length, done, position: cur >= 0 ? cur + 1 : q.length }
}

export function callReducer(s: CallState, e: CallEvent): CallState {
  switch (e.type) {
    case 'START':
      return {
        ...initialCall,
        view: 'open',
        phase: 'starting',
        callId: e.callId,
        machine: e.machine,
        speech: e.speech,
        startedAt: e.at,
      }
    case 'STARTED':
      return { ...s, runId: e.runId }
    case 'INTERIM':
      return { ...s, interim: e.text }
    case 'HEARD':
      return addCaption({ ...s, interim: '' }, { id: e.id, who: 'you', text: e.text })
    case 'LOCAL_SAY':
      return addCaption(s, { id: e.id, who: 'viko', text: e.text, local: true })
    case 'CUT': {
      // Mark the last Viko caption where the user cut in.
      const i = s.captions.findLastIndex((c) => c.who === 'viko')
      if (i < 0) return s
      const captions = [...s.captions]
      captions[i] = {
        ...captions[i]!,
        live: false,
        cut: { heard: `${e.cut.heard} ${e.cut.cutAt}`.trim(), unheard: e.cut.unheard },
      }
      return { ...s, captions, agent: 'idle' }
    }
    case 'LISTEN':
      return { ...s, listening: e.on }
    case 'MUTE':
      return { ...s, muted: e.on, listening: e.on ? false : s.listening }
    case 'PTT':
      return { ...s, ptt: e.on }
    case 'SPEAKING':
      return { ...s, agent: e.on ? 'speaking' : s.agent === 'speaking' ? 'idle' : s.agent }
    case 'MARK':
      return { ...s, local: { ...s.local, [e.key]: e.as } }
    case 'SECURE_STATE':
      return {
        ...s,
        secure: s.secure.map((x) => (e.ids.includes(x.id) ? { ...x, state: e.state } : x)),
      }
    case 'SHOW':
      return e.target ? { ...s, show: e.target } : { ...s, show: undefined }
    case 'ENDED':
      return {
        ...s,
        phase: 'ended',
        listening: false,
        ptt: false,
        agent: 'idle',
        interim: '',
        view: 'open',
        endedAt: s.endedAt ?? e.at ?? Date.now(),
      }
    case 'ERROR':
      return { ...s, phase: s.phase === 'live' ? 'live' : 'error', error: e.message }
    case 'BLOCKED':
      // A live call keeps going; anything else shows why it can't start.
      if (s.phase === 'live') return { ...s, error: e.blocker.detail ?? e.blocker.kind }
      return { ...initialCall, phase: 'blocked', blocker: e.blocker, view: 'open' }
    case 'VIEW':
      return { ...s, view: e.view }
    case 'RESET':
      return initialCall
    case 'FRAME':
      return onFrame(s, e.frame)
  }
}

function onFrame(s: CallState, f: CallFrame): CallState {
  switch (f.t) {
    case 'ready':
      return { ...s, phase: 'live', whisper: f.whisper }
    case 'brief':
      return { ...s, brief: f.brief }
    case 'brief.delta': {
      const next = s.brief ? applyCallBriefDelta(s.brief, f.delta) : undefined
      return next ? { ...s, brief: next } : s
    }
    case 'queue': {
      // A daemon-confirmed answer replaces the local override.
      const local = { ...s.local }
      for (const q of f.items) if (q.state === 'answered') delete local[q.key]
      return { ...s, queue: f.items, local, queueSeen: true }
    }
    case 'say': {
      if (f.final) {
        const captions = s.captions.map((c) =>
          c.id === `t:${f.turnId}` ? { ...c, live: false } : c,
        )
        return { ...s, captions, turnId: undefined }
      }
      if (!f.text) return s
      const id = `t:${f.turnId}`
      const i = s.captions.findIndex((c) => c.id === id)
      if (i < 0)
        return addCaption({ ...s, turnId: f.turnId }, { id, who: 'viko', text: f.text, live: true })
      const captions = [...s.captions]
      const c = captions[i]!
      // A cut caption keeps what was heard; later chunks were never spoken.
      captions[i] = c.cut
        ? { ...c, cut: { ...c.cut, unheard: `${c.cut.unheard} ${f.text}`.trim() } }
        : { ...c, text: `${c.text} ${f.text}` }
      return { ...s, captions }
    }
    case 'state':
      if (f.state === 'ended')
        return {
          ...s,
          phase: 'ended',
          agent: 'idle',
          listening: false,
          view: 'open',
          endedAt: s.endedAt ?? Date.now(),
        }
      if (f.state === 'error') return { ...s, error: f.note ?? 'The call agent stopped.' }
      return {
        ...s,
        agent: f.state === 'thinking' ? 'thinking' : s.agent === 'speaking' ? 'speaking' : 'idle',
      }
    case 'show':
      return { ...s, show: f.target }
    case 'secure':
      return { ...s, secure: f.items }
    case 'notify':
      return addCaption(s, { id: `n:${f.id}`, who: 'viko', text: f.text })
    case 'filed':
      return addCaption(s, { id: `f:${f.feedbackId}`, who: 'viko', text: `Filed: ${f.summary}` })
    case 'act':
    case 'transcript':
      return s
  }
}

/** What a call did, for the ended summary. */
export interface CallSummary {
  durationMs: number
  answered: number
  skipped: number
  /** Passkey actions approved during the call. */
  approved: number
  /** Passkey actions still waiting (approve them from the summary). */
  waiting: CallSecureItem[]
  /** Items that still need you (skipped ones too), in queue order. */
  open: CallQueueItem[]
  /** Nothing was answered, skipped, approved or queued: the summary can close by itself. */
  quiet: boolean
}

export function callSummary(s: CallState, now = Date.now()): CallSummary {
  const q = effectiveQueue(s)
  const answered = q.filter((x) => x.state === 'answered').length
  const skipped = q.filter((x) => x.state === 'skipped').length
  const approved = s.secure.filter((x) => x.state === 'approved').length
  const waiting = s.secure.filter((x) => x.state === 'queued')
  const touched = s.secure.filter((x) => x.state !== 'removed').length
  const open = q.filter(
    (x) =>
      (x.state === 'pending' || x.state === 'current' || x.state === 'skipped') &&
      (x.kind === 'decision' || x.kind === 'blocker'),
  )
  return {
    durationMs: s.startedAt ? Math.max(0, (s.endedAt ?? now) - s.startedAt) : 0,
    answered,
    skipped,
    approved,
    waiting,
    open,
    quiet: answered + skipped + touched === 0,
  }
}
